import React, { useState, useMemo } from "react";
import { ChevronRight, BookOpen, Zap } from "lucide-react";
import { allQuestionsList } from "../data/question_bank";
import {
  initialIrtState, updateTheta, selectNext, getStdError, thetaToScore,
} from "../utils/adaptiveEngine";
import { getAbilityLabel } from "../utils/irtScore";
import ScorePanel from "./ScorePanel";
import { recordAttempt } from "../data/statsRepository";

const OPTION_LABELS = ["A", "B", "C", "D"];
const MAX_QUESTIONS  = 20;
const SE_THRESHOLD   = 0.35;

const DIFFICULTY_STARS = { 'J1+': 4, 'J1': 3, 'J2': 2, 'J3': 1 };
const TYPE_LABELS    = { grammar: "语法", vocab: "词汇", expression: "表现力" };
const SECTION_LABELS = { listening: "聴解", listeningReading: "聴読解", reading: "読解" };

// ── 小组件 ────────────────────────────────────────────

function DifficultyStars({ difficulty }) {
  const lit = DIFFICULTY_STARS[difficulty] ?? 2;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`text-sm ${i < lit ? "text-yellow-400" : "text-gray-200"}`}>★</span>
      ))}
    </div>
  );
}

function ScoreBar({ score }) {
  const pct = ((score - 200) / 500) * 100;
  const color =
    score >= 580 ? "#0d9488" :
    score >= 490 ? "#16a34a" :
    score >= 410 ? "#ca8a04" :
    score >= 340 ? "#ea580c" : "#dc2626";
  return (
    <div className="w-full">
      <div className="flex justify-between text-[10px] text-gray-400 mb-1">
        <span>200</span><span>450</span><span>700</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function PrecisionBadge({ se }) {
  if (se === Infinity) return <span className="text-xs text-gray-400">精度 —</span>;
  if (se < SE_THRESHOLD) return <span className="text-xs font-semibold text-teal-600">精度 极高</span>;
  if (se < 0.5)          return <span className="text-xs font-semibold text-green-600">精度 高</span>;
  if (se < 0.8)          return <span className="text-xs font-semibold text-amber-600">精度 中</span>;
  return                        <span className="text-xs font-semibold text-red-500">精度 低</span>;
}

// ── 主组件 ───────────────────────────────────────────

export default function AdaptiveView() {
  const pool = allQuestionsList;

  const [irtState, setIrtState]         = useState(initialIrtState);
  const [record, setRecord]             = useState([]);          // [{question, userAnswer, isCorrect}]
  const [currentQ, setCurrentQ]         = useState(() => selectNext(0, pool, new Set()));
  const [selectedOption, setSelectedOption] = useState(null);
  const [finished, setFinished]         = useState(false);

  // 已答 id 集合（从 record 派生，避免重复状态）
  const answeredIds = useMemo(() => new Set(record.map(r => r.question.id)), [record]);

  const answered   = selectedOption !== null;
  const isCorrect  = answered && currentQ && selectedOption === currentQ.answer;

  const score  = thetaToScore(irtState.theta);
  const se     = getStdError(irtState.theta, record.map(r => r.question));
  const label  = getAbilityLabel(score);

  const stats = useMemo(() => {
    const correct = record.filter(r => r.isCorrect).length;
    const total   = record.length;
    return {
      correct,
      totalErrors: total - correct,
      rate: total > 0 ? ((correct / total) * 100).toFixed(1) : "0.0",
    };
  }, [record]);

  // ── 事件处理 ─────────────────────────────────────

  function handleSelect(idx) {
    if (answered || finished) return;
    setSelectedOption(idx);
  }

  function handleNext() {
    if (!answered || !currentQ) return;

    const isC       = selectedOption === currentQ.answer;
    recordAttempt(null, currentQ.id, selectedOption, isC);
    const newState  = updateTheta(irtState, currentQ, isC);
    const newRecord = [...record, { question: currentQ, userAnswer: selectedOption, isCorrect: isC }];
    const newIds    = new Set([...answeredIds, currentQ.id]);

    setIrtState(newState);
    setRecord(newRecord);

    // 停止判断
    const newSe = getStdError(newState.theta, newRecord.map(r => r.question));
    if (newRecord.length >= MAX_QUESTIONS || newSe < SE_THRESHOLD) {
      setFinished(true);
      return;
    }

    const next = selectNext(newState.theta, pool, newIds);
    if (!next) { setFinished(true); return; }

    setCurrentQ(next);
    setSelectedOption(null);
  }

  function handleRestart() {
    const first = selectNext(0, pool, new Set());
    setIrtState(initialIrtState());
    setRecord([]);
    setCurrentQ(first);
    setSelectedOption(null);
    setFinished(false);
  }

  // ── 结果页 ───────────────────────────────────────

  if (finished) {
    const finalAnswers   = Object.fromEntries(record.map(r => [r.question.id, r.userAnswer]));
    const finalQuestions = record.map(r => r.question);

    const typeStats = {};
    record.forEach(r => {
      const t = r.question.type;
      if (!typeStats[t]) typeStats[t] = { correct: 0, total: 0 };
      typeStats[t].total++;
      if (r.isCorrect) typeStats[t].correct++;
    });

    return (
      <div className="flex gap-4 flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="border-2 border-indigo-300 rounded-2xl overflow-hidden mb-4">
            <div className="px-6 py-4 bg-gradient-to-b from-indigo-50 to-white">
              <p className="text-lg font-bold text-gray-800 mb-0.5">测试完成</p>
              <p className="text-xs text-gray-400">基于 IRT 自适应算法的能力估算结果 · 共 {record.length} 题</p>
            </div>
            <div className="px-6 pb-5">
              <ScorePanel questions={finalQuestions} answers={finalAnswers} />
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-4 mb-4 bg-white">
            <p className="text-sm font-semibold text-gray-700 mb-3">题型正确率明细</p>
            <div className="space-y-2.5">
              {Object.entries(typeStats).map(([type, s]) => (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-14 shrink-0">{TYPE_LABELS[type] || type}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-400 rounded-full transition-all"
                      style={{ width: `${s.total > 0 ? (s.correct / s.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-24 text-right shrink-0">
                    {s.correct}/{s.total}（{s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0}%）
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleRestart}
            className="w-full py-2.5 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition-colors"
          >
            重新测试
          </button>
        </div>

        <HistoryPanel record={record} stats={stats} />
      </div>
    );
  }

  // ── 答题页 ───────────────────────────────────────

  return (
    <div className="flex gap-4 flex-1 min-w-0">
      <div className="flex-1 min-w-0">

        {/* 实时分数条 */}
        <div className="border border-gray-200 rounded-xl px-4 py-3 mb-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-amber-500">{score}</span>
              <span className="text-xs text-gray-400">{label}</span>
            </div>
            <div className="flex items-center gap-3">
              <PrecisionBadge se={se} />
              <span className="text-xs text-gray-400">{record.length} / {MAX_QUESTIONS} 题</span>
            </div>
          </div>
          <ScoreBar score={score} />
        </div>

        {/* 题目卡片 */}
        <div className="border-2 border-indigo-300 rounded-2xl overflow-hidden mb-3">
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-indigo-100">
            <div className="flex items-center gap-1.5">
              <Zap size={13} className="text-indigo-400" />
              <span className="text-xs text-indigo-500 font-semibold">自适应推题</span>
            </div>
            <span className="text-xs text-gray-400">第 {record.length + 1} 题</span>
          </div>

          <div className="bg-gradient-to-b from-indigo-50 to-white px-8 py-5 min-h-44">
            {currentQ && (
              <div>
                <p className="font-bold text-gray-800 mb-3">{currentQ.number}</p>
                {currentQ.lines.map((line, i) => (
                  <p key={i} className="text-sm text-gray-700 leading-relaxed mb-1">{line}</p>
                ))}
                <div className="mt-4 space-y-2">
                  {currentQ.options.map((opt, i) => {
                    const lbl        = OPTION_LABELS[i];
                    const isSelected = selectedOption === i;
                    const isCorrectOpt = currentQ.answer === i;

                    let optStyle = "border-gray-200 text-gray-700 bg-white hover:bg-gray-50";
                    if (answered) {
                      if (isCorrectOpt)       optStyle = "border-green-400 bg-green-50 text-green-700";
                      else if (isSelected)    optStyle = "border-red-400 bg-red-50 text-red-600";
                    } else if (isSelected) {
                      optStyle = "border-indigo-400 bg-indigo-50 text-indigo-700";
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        disabled={answered}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg border text-sm transition-colors ${optStyle} disabled:cursor-default`}
                      >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          answered && isCorrectOpt  ? "bg-green-500 text-white" :
                          answered && isSelected    ? "bg-red-400 text-white" :
                          "bg-gray-100 text-gray-500"
                        }`}>
                          {lbl}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 反馈栏 */}
        {answered && currentQ && (
          <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-gray-600">
                正确答案 <strong className="text-green-600">{OPTION_LABELS[currentQ.answer]}</strong>
                　您选择 <strong className={isCorrect ? "text-green-600" : "text-red-500"}>
                  {OPTION_LABELS[selectedOption]}
                </strong>
                {isCorrect
                  ? <span className="ml-1 text-green-500">✓</span>
                  : <span className="ml-1 text-red-400">✗</span>}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
                <BookOpen size={12} />
                <span>{currentQ.knowledgePoint}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                难度 <DifficultyStars difficulty={currentQ.difficulty} />
              </span>
            </div>
            {currentQ.explanation && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-indigo-600 mb-1.5">AI解析</p>
                <p className="text-xs text-gray-600 leading-relaxed">{currentQ.explanation}</p>
              </div>
            )}
            <div className="flex justify-end mt-3">
              <button
                onClick={handleNext}
                className="flex items-center gap-1 text-sm text-indigo-600 font-semibold hover:text-indigo-700"
              >
                {record.length + 1 >= MAX_QUESTIONS ? "查看结果" : "下一题"}
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 右侧面板 */}
      <div className="w-52 shrink-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-800">答题记录</span>
          <button onClick={handleRestart} className="text-xs text-red-500 hover:text-red-600">重新测试</button>
        </div>

        <HistoryPanel record={record} stats={stats} current={record.length} />
      </div>
    </div>
  );
}

// ── 答题历史面板（答题中和结束页共用）───────────────────

function HistoryPanel({ record, stats, current }) {
  const sectionCounts = { listening: 0, listeningReading: 0, reading: 0 };
  record.forEach(r => { sectionCounts[r.question.section]++; });

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <p className="text-xs text-gray-400 font-medium mb-2">自适应序列</p>
        <div className="grid grid-cols-5 gap-1.5">
          {record.map((r, idx) => (
            <div
              key={r.question.id}
              title={`${r.question.difficulty} · ${TYPE_LABELS[r.question.type]}`}
              className={`w-8 h-8 rounded-md text-xs font-semibold flex items-center justify-center ${
                r.isCorrect ? "bg-green-500 text-white" : "bg-red-400 text-white"
              }`}
            >
              {idx + 1}
            </div>
          ))}
          {current !== undefined && current < MAX_QUESTIONS && (
            <div className="w-8 h-8 rounded-md text-xs font-semibold flex items-center justify-center bg-indigo-100 text-indigo-700 ring-1 ring-indigo-400">
              {current + 1}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">答对</span>
          <span className="font-semibold text-green-600">{stats.correct} 题</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">答错</span>
          <span className="font-semibold text-red-500">{stats.totalErrors} 题</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">正确率</span>
          <span className="font-semibold text-gray-700">{stats.rate}%</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <p className="text-xs text-gray-400 font-medium mb-2">题目分布</p>
        {Object.entries(SECTION_LABELS).map(([sec, lbl]) => (
          <div key={sec} className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{lbl}</span>
            <span className="font-medium">{sectionCounts[sec]} 题</span>
          </div>
        ))}
      </div>
    </>
  );
}
